<template>
  <div
    class="multi-size-group"
    :class="{
      'multi-size-group--simple': !showMultiSize || !showTitle
    }"
  >
    <div v-if="title && showMultiSizeUI" class="multi-size-group__group-title">
      {{ formattedTitle }}
    </div>
    <button
      v-if="showMultiSizeUI && showDeleteButton"
      type="button"
      class="multi-size-group__delete-button"
      @click="handleDelete"
    >
      <UseSvg icon="modal-close-big" class="multi-size-group__delete-button-icon" />
    </button>
    <slot />
    <div v-if="showMultiSizeUI" class="multi-size-group__footer">
      <div class="multi-size-group__footer-text">
        <span class="multi-size-group__footer-text-bold">
          {{ t("transfer", count) }}{{ price ? ":" : "" }}
        </span>
        <span
          v-if="price"
          class="multi-size-group__footer-text-price"
          :class="{ 'multi-size-group__footer-text-price--loading': isPricingLoading }"
        >
          {{ n(price, "currency") }}</span
        >
      </div>
      <div v-if="isPreset" class="multi-size-group__footer-link" @click="openSizeGuide">
        Our Sizing Guide
      </div>
    </div>
    <slot name="footer" />
  </div>
</template>
<script>
import { mapState } from "pinia";
import { useI18n } from "vue-i18n";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { n } from "../../vue-cart/helpers/currency";
import { getTitleCaseString } from "../utils";
import { useDtfStore } from "../store";

export default {
  name: "MultiSizeGroup",
  components: {
    UseSvg
  },
  props: {
    title: {
      type: String,
      default: ""
    },
    isPreset: {
      type: Boolean,
      default: false
    },
    count: {
      type: Number,
      default: 0
    },
    price: {
      type: [Number, String],
      default: 0
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showDeleteButton: {
      type: Boolean,
      default: true
    }
  },
  emits: ["delete", "open-size-guide"],
  setup() {
    const { t } = useI18n();
    return { n, t };
  },
  computed: {
    ...mapState(useDtfStore, ["showMultiSize", "isPricingLoading"]),
    showMultiSizeUI() {
      return this.showMultiSize && this.showTitle;
    },
    formattedTitle() {
      return getTitleCaseString(this.title);
    },
    presetName() {
      return this.isPreset ? this.title : "custom";
    }
  },
  methods: {
    handleDelete() {
      this.$emit("delete", {
        preset: this.presetName
      });
    },
    openSizeGuide() {
      submitDtfEvent("our_sizing_guide_pressed", {
        placement: this.presetName
      });
      this.$emit("open-size-guide", this.presetName);
    }
  }
};
</script>
<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.multi-size-group {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 2px solid $crafterpay-gray-modern-300;
  border-radius: 8px;
  padding: 22px 10px 10px 10px;
  margin-top: 30px;

  &:focus-within {
    border-color: $blue;
  }

  &--simple {
    border: none;
    border-radius: 8px;
    padding: 0;
    margin-top: 0;
  }

  &__group-title {
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
    color: #fff;
    background-color: $blue;
    padding: 3px 5px;
    border-radius: 10px;
    border: 3px solid $white;
    position: absolute;
    top: -15px;
    left: 10px;
  }

  &__delete-button {
    position: absolute;
    height: 25px;
    width: 25px;
    top: -10px;
    right: -5px;
    background-color: $white;
    border: none;
    border-radius: 50%;
    box-shadow: rgba($black, 0.16) 0px 1px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
      background-color: $gray-lightest;
    }
  }

  &__delete-button-icon {
    height: 15px;
    width: 15px;
    color: $text-black;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 3px;
  }

  &__footer-text-bold {
    font-size: 13px;
    font-weight: 600;
    line-height: 13px;
    text-align: center;
    color: $crafterpay-gray-modern-600;
  }

  &__footer-text-price {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    text-align: right;

    &--loading {
      opacity: 0.25;
    }
  }

  &__footer-text {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 3px;
  }

  &__footer-link {
    font-size: 13px;
    font-weight: 400;
    line-height: 14.95px;
    text-align: right;
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
